import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react'; 

const Header = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false); 
  const [isScrolled, setIsScrolled] = useState(false);

  // 1. Pages jinka top section light/white background ka hai
  const currentPath = location.pathname.toLowerCase();
  const isLightPage = currentPath === '/team' || currentPath === '/branches';

  // 2. Scroll listener
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Header ko dark text render karna hai ya white
  const shouldShowDarkText = isScrolled || isLightPage;

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'ABOUT US', path: '/about' },
    { name: 'TOUR', path: 'https://www.expresstravelcorp.com/#', isExternal: true }, 
    { name: 'OUR FLEET', path: '/luxury-car' },
    { name: 'CLIENTS', path: '/clients' },
    { name: 'TEAM', path: '/team' },
    { name: 'BRANCHES', path: '/branches' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-[1000] transition-all duration-300">
      {/* Background container */}
      <div 
        className={`w-full px-4 md:px-10 py-4 flex items-center justify-between relative transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-md shadow-lg border-b border-white/20' 
            : isLightPage 
              ? 'bg-white/50 backdrop-blur-sm border-b border-gray-200/50' // Light pages (Team/Branches) par halka blur
              : 'bg-transparent border-b border-white/10'
        }`}
      >
        {/* Logo Section */}
        <Link to="/" className="flex items-center z-[1001]">
          <img 
            src="/LOGO.jpg"
            alt="ETC Logo" 
            className="h-10 md:h-14 w-auto object-contain" 
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex gap-8 items-center ml-auto">
          {navLinks.map((link) => {
            // Text Color Logic based on scroll and current page
            const isActive = location.pathname === link.path;
            
            let textColor = '';
            if (shouldShowDarkText) {
              textColor = isActive ? 'text-[#1C4D8D]' : 'text-black hover:text-[#1C4D8D]';
            } else {
              textColor = isActive ? 'text-[#1C4D8D]' : 'text-white hover:text-gray-200';
            }

            if (link.isExternal) {
              return (
                <a
                  key={link.name}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-[13px] font-black tracking-widest transition-all ${
                    shouldShowDarkText ? 'text-black hover:text-[#1C4D8D]' : 'text-white hover:text-gray-200'
                  }`}
                >
                  {link.name}
                </a>
              );
            }
            
            return (
              <Link 
                key={link.name}
                to={link.path} 
                className={`text-[13px] font-black tracking-widest transition-all ${textColor}`}
              >
                {link.name}
              </Link>
            );
          })}
          
          {/* Contact Button */}
          <Link 
            to="/contact" 
            className={`ml-6 px-8 py-3 rounded-none font-black text-[11px] tracking-[0.2em] transition-all shadow-lg uppercase ${
              shouldShowDarkText 
                ? 'bg-[#1C4D8D] text-white hover:bg-black' 
                : 'bg-white text-[#1C4D8D] hover:bg-opacity-90'
            }`}
          >
            Contact Us
          </Link>
        </div>

        {/* Mobile Menu Toggle Button */}
        <button 
          className={`lg:hidden p-2 z-[1001] rounded-lg transition-all ${
            shouldShowDarkText ? 'text-[#1C4D8D] bg-gray-100' : 'text-white bg-black/20 backdrop-blur-sm'
          }`}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>

        {/* Mobile Overlay */}
        <div className={`
          lg:hidden fixed inset-0 h-screen w-full bg-white/95 backdrop-blur-xl z-[1000] transition-all duration-500 flex flex-col items-center justify-center gap-6
          ${isOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}
        `}>
          <div className="flex flex-col items-center gap-8 w-full px-10">
            {navLinks.map((link) => {
              if (link.isExternal) {
                return (
                  <a
                    key={link.name}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsOpen(false)}
                    className="text-2xl font-black tracking-[0.1em] uppercase text-slate-800 hover:text-[#1C4D8D]"
                  >
                    {link.name}
                  </a>
                );
              }

              return (
                <Link 
                  key={link.name}
                  to={link.path} 
                  onClick={() => setIsOpen(false)}
                  className={`text-2xl font-black tracking-[0.1em] uppercase ${
                    location.pathname === link.path ? 'text-[#1C4D8D]' : 'text-slate-800'
                  }`}
                >
                  {link.name}
                </Link>
              );
            })}
            
            <Link 
              to="/contact" 
              onClick={() => setIsOpen(false)}
              className="mt-6 bg-[#1C4D8D] text-white w-full py-5 rounded-none text-center font-black text-lg tracking-[0.2em] uppercase shadow-2xl"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;